import axios from 'axios';
import { installRequestId } from '../utils/requestId';
import { resolveMonolithApiBaseUrl, getMarketplaceDeployOrigin } from './resolveApiBaseUrl';

const API_BASE_URL = resolveMonolithApiBaseUrl(
  ['REACT_APP_NEGOTIATIONS_API_URL', 'REACT_APP_API_BASE_URL'],
  'http://localhost:8001',
);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with requests (httpOnly cookies)
});
installRequestId(api);

// Request interceptor - cookies are sent automatically by browser
// Since we're using the same domain (flea-marketplace.com), cookies will be sent properly
api.interceptors.request.use(
  (config) => {
    // Cookies are automatically sent when withCredentials: true
    // No need to manually add Authorization header since we're on the same domain
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Refresh token is in httpOnly cookie, sent automatically
        // Use same domain for token refresh to ensure cookies are sent
        const accountsApiUrl = process.env.REACT_APP_API_BASE_URL ||
          getMarketplaceDeployOrigin() || 'http://localhost:8001';
        await axios.post(
          `${accountsApiUrl}/api/token/refresh/`,
          {},  // No body needed - refresh token in cookie
          { withCredentials: true }  // Send cookies
        );
        // New tokens are set in httpOnly cookies by backend
        // No need to read response - tokens in cookies automatically
        // Retry original request (cookies will be sent automatically)
        return api(originalRequest);
      } catch (refreshError) {
        // Only redirect to login if it's a persistent auth failure
        // Don't redirect on network errors or temporary issues
        if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
          // Redirect to login - cookies will be cleared by backend
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export const negotiationsAPI = {
  normalizeNegotiationId: (negotiationRef) => {
    const raw = String(negotiationRef || '').trim();
    if (!raw) return '';
    const m = /^NEG-(\d+)$/i.exec(raw);
    return m ? m[1] : raw;
  },

  buildNegotiationRef: (id) => `NEG-${id}`,

  // Start a new negotiation
  startNegotiation: async (itemId, sellerId, listingType = 'item') => {
    const body = { item_id: itemId, listing_type: listingType };
    // seller_id optional — backend resolves from item (needed for anonymous listings).
    if (sellerId != null && sellerId !== '') {
      body.seller_id = sellerId;
    }
    const response = await api.post('/api/negotiations/start/', body);
    return response.data;
  },

  // Get active negotiations
  getActiveNegotiations: async () => {
    const response = await api.get('/api/negotiations/active/');
    return response.data;
  },

  // Get negotiation details
  getNegotiationDetail: async (id) => {
    const negotiationId = negotiationsAPI.normalizeNegotiationId(id);
    const response = await api.get(`/api/negotiations/${negotiationId}/`);
    return response.data;
  },

  // Get messages in a negotiation
  getMessages: async (id) => {
    const negotiationId = negotiationsAPI.normalizeNegotiationId(id);
    const response = await api.get(`/api/negotiations/${negotiationId}/messages/`);
    return response.data;
  },

  // Send a message
  sendMessage: async (id, content) => {
    const negotiationId = negotiationsAPI.normalizeNegotiationId(id);
    const response = await api.post(`/api/negotiations/${negotiationId}/messages/`, {
      content,
    });
    return response.data;
  },

  // Make an offer
  makeOffer: async (id, pointsAmount) => {
    const negotiationId = negotiationsAPI.normalizeNegotiationId(id);
    const response = await api.post(`/api/negotiations/${negotiationId}/offer/`, {
      points_amount: pointsAmount,
    });
    return response.data;
  },

  // Accept an offer
  acceptOffer: async (id) => {
    const negotiationId = negotiationsAPI.normalizeNegotiationId(id);
    const response = await api.post(`/api/negotiations/${negotiationId}/accept/`);
    return response.data;
  },

  // Reject an offer
  rejectOffer: async (id) => {
    const negotiationId = negotiationsAPI.normalizeNegotiationId(id);
    const response = await api.post(`/api/negotiations/${negotiationId}/reject/`);
    return response.data;
  },

  // Get unread notifications count
  getUnreadNotificationsCount: async () => {
    const response = await api.get('/api/negotiations/notifications/');
    const notifications = Array.isArray(response.data) ? response.data : [];
    return notifications.filter((n) => !n.is_read).length;
  },

  // Get all notifications
  getNotifications: async () => {
    const response = await api.get('/api/negotiations/notifications/');
    return response.data || [];
  },

  // Mark notification as read
  markNotificationRead: async (notificationId) => {
    const response = await api.post('/api/negotiations/notifications/mark-read/', {
      notification_id: notificationId,
    });
    return response.data;
  },

  // Clear badge for completed/seen offer outcomes when opening negotiations inbox
  markSeenOutcomes: async () => {
    const response = await api.post('/api/negotiations/notifications/mark-seen-outcomes/');
    return response.data;
  },
};

export default negotiationsAPI;

